// src/pages/CarsPage.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAuditedClient } from '../utils/auditClient';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { Button, DangerButton } from '../ui';
import { ProposeChange } from '../graphqlOps';
import { uploadData, getUrl } from 'aws-amplify/storage';
import { useCountry } from '../context/CountryContext';
import { Pencil, Trash2, Wrench } from 'lucide-react';

const client = createAuditedClient('CarsPage');

/* ===================== Constants ===================== */
// Enum-backed options (values match your schema enums exactly)
const FUEL_TYPES = [
  { label: 'Petrol', value: 'PETROL' },
  { label: 'Diesel', value: 'DIESEL' },
  { label: 'Hybrid', value: 'HYBRID' },
  { label: 'Electric', value: 'ELECTRIC' },
];
const TRANSMISSIONS = [
  { label: 'Automatic', value: 'AUTOMATIC' },
  { label: 'Manual', value: 'MANUAL' },
];
const BODY_TYPES = [
  { label: 'Hatchback', value: 'HATCHBACK' },
  { label: 'Sedan', value: 'SEDAN' },
  { label: 'Van', value: 'VAN' },
  { label: 'Mini van', value: 'MINIVAN' },
  { label: 'SUV', value: 'SUV' },
];
const CATEGORIES = [
  { label: 'ECONOMY', value: 'ECONOMY' },
  { label: 'COMPACT', value: 'COMPACT' },
  { label: 'COMPACT ELITE', value: 'COMPACT_ELITE' },
  { label: 'COMPACT SUV (CFAR)', value: 'COMPACT_SUV_CFAR' },
  { label: 'INTERMEDIATE', value: 'INTERMEDIATE' },
  { label: 'INTERMEDIATE ELITE', value: 'INTERMEDIATE_ELITE' },
  { label: 'INTERMEDIATE SUV', value: 'INTERMEDIATE_SUV' },
  { label: 'SUV ELITE', value: 'SUV_ELITE' },
  { label: 'MINI VAN', value: 'MINIVAN' },
];

const COLOR_OPTIONS = [
  { label: 'White', value: 'White', swatch: '#ffffff', border: '#d1d5db' },
  { label: 'Black', value: 'Black', swatch: '#000000', border: '#111827' },
  { label: 'Silver', value: 'Silver', swatch: '#e5e7eb', border: '#9ca3af' },
  { label: 'Grey', value: 'Grey', swatch: '#9ca3af', border: '#6b7280' },
  { label: 'Brown', value: 'Brown', swatch: '#7c4a2d', border: '#5a371f' },

  { label: 'Blue', value: 'Blue', swatch: '#1d4ed8', border: '#1d4ed8' },
  { label: 'Red', value: 'Red', swatch: '#dc2626', border: '#b91c1c' },
  { label: 'Green', value: 'Green', swatch: '#16a34a', border: '#15803d' },
  { label: 'Yellow', value: 'Yellow', swatch: '#eab308', border: '#ca8a04' },
  { label: 'Other', value: 'Other', swatch: '#f3f4f6', border: '#d1d5db' },
];


const LABEL_BY_VALUE = Object.fromEntries(
  [...FUEL_TYPES, ...TRANSMISSIONS, ...BODY_TYPES, ...CATEGORIES].map(o => [o.value, o.label])
);

const THIS_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 35 }, (_, i) => THIS_YEAR - i); // 2025 .. 1991

const CAR_PAGE_SIZE = 10; // <= max cars per page
const MAX_IMAGE_MB = 5;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/* ===================== GraphQL ===================== */
const ListCompanies = /* GraphQL */ `
  query ListCompanies {
    listCompanies(limit: 1000) {
      items { id name country createdAt }
      nextToken
    }
  }
`;

const GetCompany = /* GraphQL */ `
  query GetCompany($id: ID!) {
    getCompany(id: $id) { id name country }
  }
`;

// Use the generic listCars with filter/limit so we can reuse it everywhere
const ListCars = /* GraphQL */ `
  query ListCars($filter: ModelCarFilterInput, $limit: Int, $nextToken: String) {
    listCars(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        CompanyId
        name
        plate
        active
        fuelType
        transmission
        year
        category
        bodyType
        color
        imageKey

        startingPrice
        seats

        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const CreateCar = /* GraphQL */ `
  mutation CreateCar($input: CreateCarInput!) {
    createCar(input: $input) {
      id CompanyId name plate active
      fuelType transmission year category bodyType
      color
      imageKey

      startingPrice
      seats

      createdAt updatedAt
    }
  }
`;

const UpdateCar = /* GraphQL */ `
  mutation UpdateCar($input: UpdateCarInput!) {
    updateCar(input: $input) {
      id CompanyId name plate active
      fuelType transmission year category bodyType
      color
      imageKey

      startingPrice
      seats

      createdAt updatedAt
    }
  }
`;



const DeleteCar = /* GraphQL */ `
  mutation DeleteCar($input: DeleteCarInput!) {
    deleteCar(input: $input) { id }
  }
`;

/* ===================== Helpers ===================== */
function currencyForCountry(country) {
  const c = String(country || '').toUpperCase();
  if (c === 'GE' || c === 'GEORGIA') return 'USD';
  if (c === 'AL' || c === 'ALBANIA') return 'EUR';
  return 'USD'; // safe default (or '' if you prefer forcing config)
}

function formatMoney(amount, currency) {
  if (amount == null || amount === '') return '';
  const n = Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  return `${n} ${currency || ''}`.trim();
}

function useClaims() {
  const [claims, setClaims] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await getCurrentUser();
        const { tokens } = await fetchAuthSession({ forceRefresh: true });
        const p = tokens?.idToken?.payload || {};
        const CompanyId = p['custom:CompanyId'] ?? p['custom:companyId'] ?? null;
        const groups = p['cognito:groups'] || [];
        if (!alive) return;
        setClaims({
          CompanyId,
          groups,
          sub: p.sub || null,
          email: p.email || null,
          name: p.name || null,
          raw: p,
        });
      } catch (e) {
        console.error('CarsPage: fetchAuthSession failed', e);
        if (!alive) return;
        setClaims({ CompanyId: null, groups: [] });
      } finally {
        if (alive) setReady(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return { claims, ready };
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);

    if (media.addEventListener) media.addEventListener('change', onChange);
    else media.addListener(onChange);

    setMatches(media.matches);
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', onChange);
      else media.removeListener(onChange);
    };
  }, [query]);

  return matches;
}

function sanitizeJson(value) {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (Number.isNaN(value)) return null;
  if (value instanceof Date) return value.toISOString();

  if (Array.isArray(value)) return value.map(sanitizeJson);

  if (value && typeof value === 'object') {
    const out = {};
    for (const k of Object.keys(value)) {
      const v = sanitizeJson(value[k]);
      if (v !== undefined) out[k] = v;
    }
    return out;
  }
  return value;
}

async function proposeCarChange(client, claims, op, targetId, payload) {
  const CompanyId = claims?.CompanyId || payload?.CompanyId;
  if (!CompanyId) {
    throw new Error('Missing CompanyId on change request');
  }

  const cleanPayloadObj = sanitizeJson(payload || {});
  const payloadJson = JSON.stringify(cleanPayloadObj); // AWSJSON wants a JSON string

  const variables = {
    input: {
      CompanyId,
      entity: 'CAR',
      op,
      targetId: targetId || null,
      payload: payloadJson,
    },
  };

  console.log('proposeCarChange variables:', variables);

  const res = await client.graphql({
    query: ProposeChange,
    variables,
    authMode: 'userPool',
  });

  console.log('proposeCarChange response:', res);

  if (res.errors?.length) {
    throw new Error(res.errors[0].message || 'proposeChange failed');
  }

  return res.data?.proposeChange || null;
}

/* ---------- Design System ---------- */
const COLORS = {
  primary: '#2563eb',
  primaryDark: '#1e40af',
  primaryLight: '#eff6ff',
  success: '#10b981',
  successLight: '#d1fae5',
  danger: '#ef4444',
  dangerLight: '#fee2e2',
  warning: '#f59e0b',
  warningLight: '#fef3c7',
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  },
};

/* ---------- shared styles ---------- */

function cardStyle() {
  return {
    border: `1px solid ${COLORS.gray[200]}`,
    borderRadius: 16,
    padding: 24,
    background: '#ffffff',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07), 0 1px 3px rgba(0, 0, 0, 0.06)',
  };
}

function rowStyle(cols = '160px 1fr') {
  return {
    display: 'grid',
    gridTemplateColumns: cols,
    gap: 14,
    alignItems: 'center',
  };
}

const labelStyle = {
  fontSize: 14,
  color: COLORS.gray[700],
  fontWeight: 600,
  letterSpacing: '0.3px',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 10,
  border: `1px solid ${COLORS.gray[300]}`,
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
  background: '#ffffff',
  transition: 'all 0.2s ease',
  color: COLORS.gray[900],
  fontFamily: 'inherit',
  '&:hover': {
    borderColor: COLORS.gray[400],
  },
  '&:focus': {
    borderColor: COLORS.primary,
    boxShadow: `0 0 0 3px ${COLORS.primaryLight}`,
  },
};

const selectStyle = {
  ...inputStyle,
  cursor: 'pointer',
  appearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
  backgroundPosition: 'right 0.5rem center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '1.5em 1.5em',
  paddingRight: '2.5rem',
};

const pillStyle = (bg, color, border) => ({
  display: 'inline-block',
  padding: '4px 10px',
  fontSize: 12,
  fontWeight: 500,
  borderRadius: 8,
  background: bg,
  color,
  border: border || '1px solid transparent',
  whiteSpace: 'nowrap',
});

function formatDate(d) {
  if (!d) return '';
  try {
    const date = new Date(d);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return d;
  }
}

/* ===================== Components ===================== */
function ImageLightbox({ src, alt, onClose }) {
  // Close on ESC + prevent background scroll
  useEffect(() => {
    if (!src) return;

    const onKeyDown = e => {
      if (e.key === 'Escape') onClose?.();
    };

    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [src, onClose]);

  if (!src) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(0,0,0,0.92)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        cursor: 'zoom-out',
        backdropFilter: 'blur(2px)',
        animation: 'fadeIn 0.2s ease-out',
      }}
    >
      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close image"
        style={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 10001,
          width: 44,
          height: 44,
          borderRadius: 999,
          border: '2px solid rgba(255,255,255,0.3)',
          background: 'rgba(0,0,0,0.5)',
          color: '#fff',
          fontSize: 20,
          lineHeight: 1,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ✕
      </button>

      {/* Image */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          maxWidth: 'min(1200px, 96vw)',
          maxHeight: '92vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'default',
          animation: 'zoomIn 0.3s ease-out',
        }}
      >
        <img
          src={src}
          alt={alt || 'Car photo'}
          style={{
            maxWidth: '100%',
            maxHeight: '92vh',
            objectFit: 'contain',
            borderRadius: 16,
            boxShadow: '0 25px 70px rgba(0,0,0,0.6)',
            background: 'rgba(255,255,255,0.03)',
          }}
        />
      </div>
    </div>
  );
}

function CarFormPanel({
  isMobile,
  isAdmin,
  canEdit,
  companies,
  companyName,
  carForm,
  setCarForm,
  carEditingId,
  carsLoading,
  onSubmitCreate,
  onSubmitUpdate,
  onReset,
  imagePreviewUrl,
  onImageChange,
  currencyForCompanyId
}) {
  const fileInputRef = useRef(null);
  const formRowCols = isMobile ? 'minmax(0, 1fr)' : '140px 1fr';
  return (
    <section style={cardStyle()}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <h4 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: COLORS.gray[900] }}>
          {carEditingId ? 'Edit Car' : 'Add New Car'}
        </h4>
      </div>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: COLORS.gray[600], lineHeight: 1.5 }}>
        Choose an agency and define car details. Non-admins submit change requests for approval.
      </p>

      <div style={{ display: 'grid', gap: 14, maxWidth: '100%' }}>
        {/* Agency */}
        <div style={rowStyle(formRowCols)}>
          <label style={labelStyle}>Agency</label>
          {isAdmin ? (
            <select
              style={selectStyle}
              value={carForm.CompanyId}
              onChange={e => setCarForm(f => ({ ...f, CompanyId: e.target.value }))}
            >
              <option value="">Select agency</option>
              {companies.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          ) : (
            <div style={{ fontSize: 13, color: '#111827' }}>
              <span>{companyName || '(your company)'}</span>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                Cars will be created under your agency.
              </div>
            </div>
          )}
        </div>

        {/* Name */}
        <div style={rowStyle(formRowCols)}>
          <label style={labelStyle}>Name</label>
          <input
            style={inputStyle}
            value={carForm.name}
            onChange={e => setCarForm(f => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Kia Rio"
          />
        </div>

        {/* Plate */}
        <div style={rowStyle(formRowCols)}>
          <label style={labelStyle}>Plate*</label>
          <input
            style={inputStyle}
            value={carForm.plate}
            onChange={e => setCarForm(f => ({ ...f, plate: e.target.value }))}
            placeholder="e.g. B-123456"
          />
        </div>

        {/* Starting price */}
        <div style={rowStyle(formRowCols)}>
          <label style={labelStyle}>Starting price (per day)</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <input
              style={inputStyle}
              type="number"
              step="0.01"
              min="0"
              value={carForm.startingPrice ?? ''}
              onChange={e => setCarForm(f => ({ ...f, startingPrice: e.target.value }))}
              placeholder="e.g. 25"
            />
            <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
              {carForm.CompanyId ? currencyForCompanyId(carForm.CompanyId) : '—'}
              {' '} / day
            </span>
          </div>
        </div>


        {/* Seats */}
        <div style={rowStyle(formRowCols)}>
          <label style={labelStyle}>Seats</label>
          <input
            style={inputStyle}
            type="number"
            step="1"
            min="1"
            value={carForm.seats ?? ''}
            onChange={e => setCarForm(f => ({ ...f, seats: e.target.value }))}
            placeholder="e.g. 5"
          />
        </div>


        {/* Fuel */}
        <div style={rowStyle(formRowCols)}>
          <label style={labelStyle}>Fuel</label>
          <select
            style={selectStyle}
            value={carForm.fuelType}
            onChange={e => setCarForm(f => ({ ...f, fuelType: e.target.value }))}
          >
            {FUEL_TYPES.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Transmission */}
        <div style={rowStyle(formRowCols)}>
          <label style={labelStyle}>Transmission</label>
          <select
            style={selectStyle}
            value={carForm.transmission}
            onChange={e => setCarForm(f => ({ ...f, transmission: e.target.value }))}
          >
            {TRANSMISSIONS.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Year */}
        <div style={rowStyle(formRowCols)}>
          <label style={labelStyle}>Year</label>
          <select
            style={selectStyle}
            value={String(carForm.year)}
            onChange={e => setCarForm(f => ({ ...f, year: Number(e.target.value) }))}
          >
            {YEARS.map(y => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        {/* Category */}
        <div style={rowStyle(formRowCols)}>
          <label style={labelStyle}>Category</label>
          <select
            style={selectStyle}
            value={carForm.category}
            onChange={e => setCarForm(f => ({ ...f, category: e.target.value }))}
          >
            {CATEGORIES.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Body */}
        <div style={rowStyle('140px 1fr')}>
          <label style={labelStyle}>Body</label>
          <select
            style={selectStyle}
            value={carForm.bodyType}
            onChange={e => setCarForm(f => ({ ...f, bodyType: e.target.value }))}
          >
            {BODY_TYPES.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Color */}
        <div style={rowStyle(formRowCols)}>
          <label style={labelStyle}>Color</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <select
              style={{ ...selectStyle, maxWidth: isMobile ? '100%' : 180 }}
              value={carForm.color || ''}
              onChange={e => setCarForm(f => ({ ...f, color: e.target.value }))}
            >
              <option value="">Select color</option>
              {COLOR_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            {carForm.color && (
              (() => {
                const meta =
                  COLOR_OPTIONS.find(c => c.value === carForm.color) ||
                  { swatch: '#f3f4f6', border: '#d1d5db' };
                return (
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: '999px',
                      border: `2px solid ${meta.border}`,
                      background: meta.swatch,
                      boxSizing: 'border-box',
                    }}
                    title={carForm.color}
                  />
                );
              })()
            )}
          </div>
        </div>


        {/* Active */}
        <div style={rowStyle(formRowCols)}>
          <label style={labelStyle}>Active</label>
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              color: '#111827',
            }}
          >
            <input
              type="checkbox"
              checked={!!carForm.active}
              onChange={e => setCarForm(f => ({ ...f, active: e.target.checked }))}
            />
            <span>Show in availability & reservations</span>
          </label>
        </div>

        {/* Image (admin only) */}
        {isAdmin && (
          <div style={rowStyle(formRowCols)}>
            <label style={labelStyle}>Image (admin)</label>
            <div>
              {/* Hidden native input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={onImageChange}
                disabled={carsLoading || !canEdit}
                style={{ display: 'none' }}
              />

              {/* Pretty button */}
              <button
                type="button"
                onClick={() => {
                  if (carsLoading || !canEdit) return;
                  fileInputRef.current?.click();
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 10px',
                  borderRadius: 999,
                  border: '1px dashed #d1d5db',
                  background: '#f9fafb',
                  fontSize: 12,
                  cursor: carsLoading || !canEdit ? 'not-allowed' : 'pointer',
                  color: '#111827',
                  transition: 'background 0.15s, border-color 0.15s, transform 0.05s',
                }}
              >
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: '999px',
                    border: '1px solid #9ca3af',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 10,
                  }}
                >
                  +
                </span>
                <span>{imagePreviewUrl ? 'Change image' : 'Upload image'}</span>
              </button>

              <div style={{ marginTop: 4, fontSize: 11, color: '#6b7280' }}>
                JPG, PNG or WebP · up to {MAX_IMAGE_MB}MB.
              </div>

              {imagePreviewUrl && (
                <div style={{ marginTop: 8 }}>
                  <img
                    src={imagePreviewUrl}
                    alt="Car"
                    style={{
                      maxWidth: 180,
                      maxHeight: 130,
                      borderRadius: 10,
                      border: '1px solid #e5e7eb',
                      objectFit: 'cover',
                      background: '#f9fafb',
                    }}
                  />
                </div>
              )}
              {carEditingId && !imagePreviewUrl && (
                <div style={{ marginTop: 4, fontSize: 11, color: '#6b7280' }}>
                  Existing image will be kept unless you choose a new file.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            justifyContent: isMobile ? 'stretch' : 'flex-end',
            flexWrap: 'wrap',
            marginTop: 8,
          }}
        >
          {carEditingId ? (
            <>
              <Button onClick={onReset} style={{ background: 'white', width: isMobile ? '100%' : undefined }}>
                Cancel
              </Button>
              <Button onClick={onSubmitUpdate} disabled={carsLoading || !canEdit} style={{ width: isMobile ? '100%' : undefined }}>
                Save changes
              </Button>
            </>
          ) : (
            <Button onClick={onSubmitCreate} disabled={carsLoading || !canEdit} style={{ width: isMobile ? '100%' : undefined }}>
              Add car
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

/** Single car row with optional thumbnail */
function CarRow({ car, coName, canEdit, startEditCar, handleDeleteCar, onOpenImage, currencyForCompanyId }) {
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (!car.imageKey) {
      setImageUrl(null);
      return;
    }

    (async () => {
      try {
        const { url } = await getUrl({ key: car.imageKey });
        if (!cancelled) {
          setImageUrl(url.toString());
        }
      } catch (err) {
        console.error('CarRow: failed to load thumbnail', err);
        if (!cancelled) setImageUrl(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [car.imageKey]);

  const activePill = car.active
    ? pillStyle('#d1fae5', '#065f46', '1px solid #6ee7b7')
    : pillStyle('#fee2e2', '#7f1d1d', '1px solid #fca5a5');
  const colorMeta = car.color
    ? COLOR_OPTIONS.find(c => c.value === car.color)
    : null;
  return (
    <div
      style={{
        border: `1px solid ${COLORS.gray[200]}`,
        borderRadius: 14,
        padding: 14,
        background: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 14,
        flexWrap: 'wrap',
        maxWidth: '100%',
        boxSizing: 'border-box',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)';
        e.currentTarget.style.border = `1px solid ${COLORS.primary}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
        e.currentTarget.style.border = `1px solid ${COLORS.gray[200]}`;
      }}
    >
      {/* Left section: image + info */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          minWidth: 0,
          flex: 1,
        }}
      >
        {imageUrl && (
          <button
            type="button"
            onClick={() => onOpenImage?.(imageUrl, car.name)}
            title="Open photo"
            style={{
              border: 'none',
              padding: 0,
              background: 'transparent',
              cursor: 'zoom-in',
              flexShrink: 0,
              borderRadius: 10,
              transition: 'transform 0.2s ease',
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <img
              src={imageUrl}
              alt={car.name}
              style={{
                width: 92,
                height: 62,
                objectFit: 'cover',
                borderRadius: 10,
                border: `1px solid ${COLORS.gray[200]}`,
                background: COLORS.gray[100],
                display: 'block',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
              }}
            />
          </button>
        )}

        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: 15,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
              color: COLORS.gray[900],
            }}
          >
            {car.name} {car.plate ? `(${car.plate})` : ''}
          </div>
          <div
            style={{
              marginTop: 6,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 7,
              fontSize: 12,
            }}
          >
            <span
              style={pillStyle(
                '#eff6ff',
                '#1e40af',
                `1px solid ${COLORS.gray[300]}`
              )}
            >
              {coName(car.CompanyId)}
            </span>
            <span style={activePill}>{car.active ? 'Active' : 'Inactive'}</span>
            {car.year && (
              <span
                style={pillStyle(
                  COLORS.gray[100],
                  COLORS.gray[700],
                  `1px solid ${COLORS.gray[300]}`
                )}
              >
                {car.year}
              </span>
            )}
            {car.category && (
              <span
                style={pillStyle(
                  '#f3f4ff',
                  '#5b21b6',
                  `1px solid ${COLORS.gray[300]}`
                )}
              >
                {LABEL_BY_VALUE[car.category] || car.category}
              </span>
            )}
            {car.fuelType && (
              <span
                style={pillStyle(
                  '#fef9c3',
                  '#b45309',
                  `1px solid ${COLORS.gray[300]}`
                )}
              >
                {LABEL_BY_VALUE[car.fuelType] || car.fuelType}
              </span>
            )}
            {car.transmission && (
              <span
                style={pillStyle(
                  '#e0f2fe',
                  '#0c4a6e',
                  `1px solid ${COLORS.gray[300]}`
                )}
              >
                {LABEL_BY_VALUE[car.transmission] || car.transmission}
              </span>
            )}
            {car.seats != null && (
              <span style={pillStyle('#ecfeff', '#164e63', `1px solid ${COLORS.gray[300]}`)}>
                {car.seats} seats
              </span>
            )}

            {car.startingPrice != null && (
              <span style={pillStyle('#dcfce7', '#166534', `1px solid ${COLORS.gray[300]}`)}>
                From {formatMoney(car.startingPrice, currencyForCompanyId(car.CompanyId))} / day
              </span>
            )}
            {car.bodyType && (
              <span
                style={pillStyle(
                  COLORS.gray[100],
                  COLORS.gray[700],
                  `1px solid ${COLORS.gray[300]}`
                )}
              >
                {LABEL_BY_VALUE[car.bodyType] || car.bodyType}
              </span>
            )}
            {car.color && (
              <span
                style={pillStyle(
                  COLORS.gray[100],
                  COLORS.gray[700],
                  `1px solid ${COLORS.gray[300]}`
                )}
              >
                {colorMeta && (
                  <span
                    style={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      borderRadius: '999px',
                      marginRight: 6,
                      background: colorMeta.swatch,
                      border: `1px solid ${colorMeta.border}`,
                      verticalAlign: 'middle',
                    }}
                  />
                )}
                {car.color}
              </span>
            )}

          </div>
        </div>
      </div>

      {/* Right section: actions */}
      {canEdit && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: 8,
            flexShrink: 0,
            alignItems: 'center',
          }}
        >
          <Button
            onClick={() => startEditCar(car)}
            style={{
              padding: '8px 10px',
              fontSize: 13,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              borderRadius: 8,
              minWidth: 32,
              minHeight: 32,
              background: COLORS.primaryLight,
              color: COLORS.primary,
              border: `1px solid ${COLORS.gray[200]}`,
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              fontWeight: 500,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = COLORS.primary;
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = COLORS.primaryLight;
              e.currentTarget.style.color = COLORS.primary;
            }}
            title="Edit car"
            aria-label="Edit car"
          >
            <Pencil size={14} />
          </Button>

          <DangerButton
            onClick={() => handleDeleteCar(car.id)}
            style={{
              padding: '8px 10px',
              fontSize: 13,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              borderRadius: 8,
              minWidth: 32,
              minHeight: 32,
              background: '#fee2e2',
              color: COLORS.danger,
              border: `1px solid ${COLORS.gray[200]}`,
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              fontWeight: 500,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = COLORS.danger;
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#fee2e2';
              e.currentTarget.style.color = COLORS.danger;
            }}
            title="Delete car"
            aria-label="Delete car"
          >
            <Trash2 size={14} />
          </DangerButton>
        </div>
      )}

    </div>
  );
}


function CarListSection({
  isMobile,
  isAdmin,
  canEdit,
  companies,
  carsLoading,
  filteredCars,
  pagedCars,
  carFilters,
  setCarFilters,
  carPage,
  totalCarPages,
  setCarPage,
  coName,
  startEditCar,
  handleDeleteCar,
  onOpenImage,
  currencyForCompanyId,
}) {
  return (
    <section style={cardStyle()}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          marginBottom: 12,
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <h4 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: COLORS.gray[900] }}>Fleet Inventory</h4>
        <div style={{ fontSize: 13, color: COLORS.gray[600], fontWeight: 500 }}>
          {filteredCars.length} car{filteredCars.length === 1 ? '' : 's'}
          {filteredCars.length > 0 && (
            <>
              {' '}
              · Page {carPage} of {totalCarPages}
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          marginBottom: 14,
        }}
      >
        {isAdmin && (
          <select
            style={{ ...selectStyle, maxWidth: isMobile ? '100%' : 200, width: isMobile ? '100%' : undefined }}
            value={carFilters.companyId}
            onChange={e => {
              const value = e.target.value;
              setCarFilters(f => ({ ...f, companyId: value }));
              setCarPage(1);
            }}
          >
            <option value="ALL">All agencies</option>
            {companies.map(c => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        )}

        <select
          style={{ ...selectStyle, maxWidth: isMobile ? '100%' : 150, width: isMobile ? '100%' : undefined }}
          value={carFilters.active}
          onChange={e => {
            const value = e.target.value;
            setCarFilters(f => ({ ...f, active: value }));
            setCarPage(1);
          }}
        >
          <option value="ALL">All statuses</option>
          <option value="ACTIVE">Active only</option>
          <option value="INACTIVE">Inactive only</option>
        </select>

        <select
          style={{ ...selectStyle, maxWidth: isMobile ? '100%' : 160, width: isMobile ? '100%' : undefined }}
          value={carFilters.fuelType}
          onChange={e => {
            const value = e.target.value;
            setCarFilters(f => ({ ...f, fuelType: value }));
            setCarPage(1);
          }}
        >
          <option value="ALL">All fuels</option>
          {FUEL_TYPES.map(o => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          style={{ ...selectStyle, maxWidth: isMobile ? '100%' : 200, width: isMobile ? '100%' : undefined }}
          value={carFilters.category}
          onChange={e => {
            const value = e.target.value;
            setCarFilters(f => ({ ...f, category: value }));
            setCarPage(1);
          }}
        >
          <option value="ALL">All categories</option>
          {CATEGORIES.map(o => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {carsLoading && (
        <div style={{ color: COLORS.gray[500], marginBottom: 12, fontSize: 14 }}>Loading cars…</div>
      )}

      <div style={{ display: 'grid', gap: 10 }}>
        {pagedCars.map(car => (
          <CarRow
            key={car.id}
            car={car}
            coName={coName}
            canEdit={canEdit}
            startEditCar={startEditCar}
            handleDeleteCar={handleDeleteCar}
            onOpenImage={onOpenImage}
            currencyForCompanyId={currencyForCompanyId}
          />
        ))}
        {filteredCars.length === 0 && !carsLoading && (
          <div style={{ color: COLORS.gray[500], fontSize: 13, padding: '20px 16px', textAlign: 'center' }}>
            No cars match the selected filters
          </div>
        )}
      </div>

      {filteredCars.length > CAR_PAGE_SIZE && (
        <div
          style={{
            marginTop: 16,
            paddingTop: 14,
            borderTop: `1px solid ${COLORS.gray[200]}`,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 10,
            fontSize: 13,
            color: COLORS.gray[600],
          }}
        >
          <Button
            onClick={() => setCarPage(p => Math.max(1, p - 1))}
            disabled={carPage === 1}
            style={{ background: 'white' }}
          >
            Previous
          </Button>
          <span style={{ fontWeight: 500 }}>
            Page {carPage} of {totalCarPages}
          </span>
          <Button
            onClick={() => setCarPage(p => Math.min(totalCarPages, p + 1))}
            disabled={carPage === totalCarPages}
            style={{ background: 'white' }}
          >
            Next
          </Button>
        </div>
      )}
    </section>
  );
}

/* ===================== Page ===================== */

export default function CarsPage() {
  const { claims, ready } = useClaims();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { selectedCompanyId: ctxCompanyId, loadingCompany, companyError } = useCountry();

  const groups = claims?.groups || [];

  const isAdmin = groups.includes('admin');
  const isCountryAdmin = groups.includes('country_admin');
  const isEditor = groups.includes('editor');

  const canEdit = isAdmin || isCountryAdmin || isEditor;


  const myCompanyId = claims?.CompanyId || null;

  const [companies, setCompanies] = useState([]);
  const [companyName, setCompanyName] = useState('');

  const carFormRef = useRef(null);

  // ==== Shared toast / confirm ====
  const [toast, setToast] = useState(null);
  const toastTimeoutRef = useRef(null);

  const [confirmState, setConfirmState] = useState(null);
  const confirmResolveRef = useRef(null);
  const [lightbox, setLightbox] = useState({ open: false, src: '', alt: '' });

  const openLightbox = useCallback((src, alt) => {
    if (!src) return;
    setLightbox({ open: true, src, alt: alt || '' });
  }, []);

  const closeLightbox = useCallback(() => {
    setLightbox({ open: false, src: '', alt: '' });
  }, []);

  function showAlert(message, variant = 'info') {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast({ message, variant });
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
    }, 3500);
  }

  function hideAlert() {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
    }
    setToast(null);
  }

  function openConfirm(options) {
    return new Promise(resolve => {
      confirmResolveRef.current = resolve;
      setConfirmState({
        title: options.title || 'Confirm action',
        message: options.message || '',
        confirmLabel: options.confirmLabel || 'Confirm',
        cancelLabel: options.cancelLabel || 'Cancel',
      });
    });
  }

  function handleConfirm(result) {
    if (confirmResolveRef.current) {
      confirmResolveRef.current(result);
      confirmResolveRef.current = null;
    }
    setConfirmState(null);
  }

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  /* ===================== Companies ===================== */

  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  // For admins, follow CountryContext. For others, it's their own company.
  const activeCompanyId = useMemo(
    () => (isAdmin ? (ctxCompanyId || myCompanyId || null) : (myCompanyId || null)),
    [isAdmin, ctxCompanyId, myCompanyId]
  );

  // Keep local selectedCompanyId in sync for admin (used by maintenance section)
  useEffect(() => {
    if (!ready) return;
    if (!isAdmin) return;
    setSelectedCompanyId(ctxCompanyId || '');
  }, [ready, isAdmin, ctxCompanyId]);

  const loadCompanies = useCallback(async () => {
    if (!ready) return;

    if (isAdmin) {
      try {
        const res = await client.graphql({ query: ListCompanies, authMode: 'userPool' });
        const items = (res.data?.listCompanies?.items || [])
          .filter(Boolean)
          .sort((a, b) => a.name.localeCompare(b.name));
        setCompanies(items);
        // Do NOT override selectedCompanyId here; CountryContext drives it
      } catch (e) {
        console.error('ListCompanies failed:', e);
        setCompanies([]);
        showAlert('Failed to load companies.', 'error');
      }
      return;
    }

    // Non-admin: only their own company
    if (!myCompanyId) {
      setCompanies([]);
      setSelectedCompanyId('');
      return;
    }

    try {
      const res = await client.graphql({
        query: GetCompany,
        variables: { id: myCompanyId },
        authMode: 'userPool',
      });
      const co = res?.data?.getCompany;
      const name = co?.name || myCompanyId;
      setCompanies(co ? [co] : [{ id: myCompanyId, name }]);
      setCompanyName(name);
      setSelectedCompanyId(myCompanyId);
    } catch (e) {
      console.error('GetCompany (non-admin) failed:', e);
      setCompanies([{ id: myCompanyId, name: myCompanyId }]);
      setCompanyName(myCompanyId);
      setSelectedCompanyId(myCompanyId);
      showAlert('Failed to load your company details.', 'error');
    }
  }, [ready, isAdmin, myCompanyId]);

  /* ===================== Cars section ===================== */

  const [cars, setCars] = useState([]);
  const [carsLoading, setCarsLoading] = useState(false);

  const [carFilters, setCarFilters] = useState({
    companyId: 'ALL',
    active: 'ALL',
    fuelType: 'ALL',
    category: 'ALL',
  });

  const [carPage, setCarPage] = useState(1);

  const [carEditingId, setCarEditingId] = useState(null);
  const [carForm, setCarForm] = useState({
    CompanyId: '',
    name: '',
    plate: '',
    active: true,
    fuelType: 'PETROL',
    transmission: 'AUTOMATIC',
    year: THIS_YEAR,
    category: 'ECONOMY',
    bodyType: 'SEDAN',
    color: 'White',

    startingPrice: '',
    seats: '',
  });


  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);

  const coName = useMemo(() => {
    const map = new Map(companies.map(c => [c.id, c.name]));
    return id => map.get(id) || id || '(unknown)';
  }, [companies]);

  const loadCars = useCallback(async () => {
    if (!ready) return;

    try {
      setCarsLoading(true);

      const variables = { limit: 1000 };

      // Always scope to activeCompanyId if we have it
      if (activeCompanyId) {
        variables.filter = { CompanyId: { eq: activeCompanyId } };
      }

      const res = await client.graphql({
        query: ListCars,
        variables,
        authMode: 'userPool',
      });

      const items = (res.data?.listCars?.items || []).filter(Boolean);

      // Already scoped by filter; just sort
      items.sort((a, b) =>
        ((a.CompanyId || '') + a.name).localeCompare((b.CompanyId || '') + b.name)
      );
      setCars(items);
      setCarPage(1);
    } catch (e) {
      console.error('ListCars failed:', e);
      setCars([]);
      showAlert('Failed to load cars.', 'error');
    } finally {
      setCarsLoading(false);
    }
  }, [ready, activeCompanyId]);


  // bootstrap companies + cars
  useEffect(() => {
    if (!ready) return;
    loadCompanies();
    loadCars();
  }, [ready, loadCompanies, loadCars]);

  // Default CompanyId in car form
  useEffect(() => {
    if (!ready) return;

    if (isAdmin) {
      setCarForm(prev => ({ ...prev, CompanyId: activeCompanyId || '' }));
    } else if (myCompanyId) {
      // country_admin, editor, staff → bound to their own company
      setCarForm(prev => ({ ...prev, CompanyId: myCompanyId }));
    }
  }, [ready, isAdmin, myCompanyId, activeCompanyId]);

  // Keep Fleet company filter in sync with activeCompanyId for admins
  useEffect(() => {
    if (!isAdmin) return;
    if (!activeCompanyId) return;

    setCarFilters(prev => {
      // already in sync → no-op
      if (prev.companyId === activeCompanyId) return prev;

      return {
        ...prev,
        companyId: activeCompanyId,
      };
    });
    // whenever company changes, reset to first page
    setCarPage(1);
  }, [isAdmin, activeCompanyId]);

  const filteredCars = useMemo(() => {
    return cars.filter(car => {
      if (isAdmin && carFilters.companyId !== 'ALL' && car.CompanyId !== carFilters.companyId) {
        return false;
      }
      if (carFilters.active === 'ACTIVE' && !car.active) return false;
      if (carFilters.active === 'INACTIVE' && car.active) return false;
      if (carFilters.fuelType !== 'ALL' && car.fuelType !== carFilters.fuelType) return false;
      if (carFilters.category !== 'ALL' && car.category !== carFilters.category) return false;
      return true;
    });
  }, [cars, carFilters, isAdmin]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredCars.length / CAR_PAGE_SIZE));
    if (carPage > maxPage) {
      setCarPage(maxPage);
    }
  }, [filteredCars.length, carPage]);

  // Scroll to car form when editing a car
  useEffect(() => {
    if (carEditingId && carFormRef.current) {
      setTimeout(() => {
        carFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [carEditingId]);

  const totalCarPages = useMemo(
    () => Math.max(1, Math.ceil(filteredCars.length / CAR_PAGE_SIZE)),
    [filteredCars.length]
  );

  const pagedCars = useMemo(
    () => filteredCars.slice((carPage - 1) * CAR_PAGE_SIZE, carPage * CAR_PAGE_SIZE),
    [filteredCars, carPage]
  );

  function handleImageChange(e) {
    if (!isAdmin) return;

    const file = e.target.files?.[0];
    if (!file) {
      // Clear selection
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
      setImageFile(null);
      setImagePreviewUrl(null);
      return;
    }

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      showAlert('Only JPG, PNG or WebP images are allowed.', 'error');
      e.target.value = '';
      return;
    }

    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      showAlert(`Max image size is ${MAX_IMAGE_MB} MB.`, 'error');
      e.target.value = '';
      return;
    }

    const objectUrl = URL.createObjectURL(file);

    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }

    setImageFile(file);
    setImagePreviewUrl(objectUrl);
  }

  async function uploadCarImageIfNeeded(companyId) {
    if (!isAdmin || !imageFile) return null;
    if (!companyId) throw new Error('uploadCarImageIfNeeded: missing CompanyId');

    const ext = (imageFile.name.split('.').pop() || 'jpg').toLowerCase();
    const key = `companies/${companyId}/cars/${Date.now()}-${Math.random()
      .toString(36)
      .slice(2)}.${ext}`;

    await uploadData({
      key,
      data: imageFile,
      options: {
        contentType: imageFile.type,
      },
    }).result;

    return key;
  }

  function validateCarForm() {
    const CompanyId = carForm.CompanyId?.trim();
    const name = carForm.name?.trim();
    const plate = carForm.plate?.trim();
    if (!CompanyId) { showAlert('Choose a company.', 'error'); return false; }
    if (!name) { showAlert('Car name is required.', 'error'); return false; }
    if (!plate) { showAlert('Plate number is required.', 'error'); return false; }

    if (carForm.startingPrice !== '' && carForm.startingPrice != null) {
      const p = Number(carForm.startingPrice);
      if (!Number.isFinite(p) || p < 0) {
        showAlert('Starting price must be a valid non-negative number.', 'error');
        return false;
      }
    }

    if (carForm.seats !== '' && carForm.seats != null) {
      const s = Number(carForm.seats);
      if (!Number.isFinite(s) || s < 1 || !Number.isInteger(s)) {
        showAlert('Seats must be a whole number (1+).', 'error');
        return false;
      }
    }

    return true;
  }

  function resetCarForm() {
    setCarEditingId(null);
    setCarForm({
      CompanyId: isAdmin ? '' : (myCompanyId || ''),
      name: '',
      plate: '',
      active: true,
      fuelType: 'PETROL',
      transmission: 'AUTOMATIC',
      year: THIS_YEAR,
      category: 'ECONOMY',
      bodyType: 'HATCHBACK',
      color: 'White',

      startingPrice: '',
      seats: '',
    });

    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setImageFile(null);
    setImagePreviewUrl(null);
  }

  async function submitCreateCar() {
    if (!canEdit) return;
    if (!validateCarForm()) return;

    const input = {
      CompanyId: carForm.CompanyId.trim(),
      name: carForm.name.trim(),
      plate: carForm.plate.trim(),
      active: !!carForm.active,
      fuelType: carForm.fuelType || null,
      transmission: carForm.transmission || null,
      year: Number(carForm.year) || null,
      category: carForm.category || null,
      bodyType: carForm.bodyType || null,
      color: carForm.color || null,
      startingPrice:
        carForm.startingPrice === '' ? null : Number(carForm.startingPrice),
      seats:
        carForm.seats === '' ? null : Number(carForm.seats),
    };

    if (isAdmin) {
      try {
        setCarsLoading(true);

        // upload image if admin selected one
        let imageKey = null;
        if (imageFile) {
          imageKey = await uploadCarImageIfNeeded(input.CompanyId);
          if (imageKey) {
            input.imageKey = imageKey;
          }
        }

        const res = await client.graphql({
          query: CreateCar,
          variables: { input },
          authMode: 'userPool',
        });
        const created = res.data?.createCar;
        setCars(prev =>
          [...prev, created].sort((a, b) =>
            (coName(a.CompanyId) + a.name).localeCompare(coName(b.CompanyId) + b.name)
          )
        );
        resetCarForm();
        showAlert('Car created.', 'success');
      } catch (e) {
        console.error('CreateCar failed:', e);
        showAlert('Create failed. Check console.', 'error');
      } finally {
        setCarsLoading(false);
      }
      return;
    }

    try {
      await proposeCarChange(client, claims, 'CREATE', null, input);
      showAlert('Car creation submitted for approval.', 'success');
      resetCarForm();
    } catch (e) {
      console.error('proposeCarChange CREATE failed:', e);
      showAlert('Change request failed. Check console.', 'error');
    }
  }

  async function submitUpdateCar() {
    if (!canEdit || !carEditingId) return;
    if (!validateCarForm()) return;

    const input = {
      id: carEditingId,
      CompanyId: carForm.CompanyId.trim(),
      name: carForm.name.trim(),
      plate: carForm.plate.trim(),
      active: !!carForm.active,
      fuelType: carForm.fuelType || null,
      transmission: carForm.transmission || null,
      year: Number(carForm.year) || null,
      category: carForm.category || null,
      bodyType: carForm.bodyType || null,
      color: carForm.color || null,
      startingPrice:
        carForm.startingPrice === '' ? null : Number(carForm.startingPrice),
      seats:
        carForm.seats === '' ? null : Number(carForm.seats),
    };

    if (isAdmin) {
      try {
        setCarsLoading(true);

        // preserve existing image if no new file, override if admin selected new
        const existing = cars.find(c => c.id === carEditingId);
        let imageKey = existing?.imageKey || null;
        if (imageFile) {
          imageKey = await uploadCarImageIfNeeded(input.CompanyId);
        }
        if (imageKey) {
          input.imageKey = imageKey;
        }

        const res = await client.graphql({
          query: UpdateCar,
          variables: { input },
          authMode: 'userPool',
        });
        const updated = res.data?.updateCar;
        setCars(prev =>
          prev
            .map(c => (c.id === updated.id ? updated : c))
            .sort((a, b) =>
              (coName(a.CompanyId) + a.name).localeCompare(coName(b.CompanyId) + b.name)
            )
        );
        resetCarForm();
        showAlert('Changes saved.', 'success');
      } catch (e) {
        console.error('UpdateCar failed:', e);
        showAlert('Update failed. Check console.', 'error');
      } finally {
        setCarsLoading(false);
      }
      return;
    }

    const payload = {
      CompanyId: carForm.CompanyId.trim(),
      name: carForm.name.trim(),
      plate: carForm.plate.trim(),
      active: !!carForm.active,
      fuelType: carForm.fuelType || null,
      transmission: carForm.transmission || null,
      year: Number(carForm.year) || null,
      category: carForm.category || null,
      bodyType: carForm.bodyType || null,
      color: carForm.color || null,
      startingPrice:
        carForm.startingPrice === '' ? null : Number(carForm.startingPrice),
      seats:
        carForm.seats === '' ? null : Number(carForm.seats),
    };

    try {
      await proposeCarChange(client, claims, 'UPDATE', carEditingId, payload);
      showAlert('Update submitted for approval.', 'success');
      resetCarForm();
    } catch (e) {
      console.error('proposeCarChange UPDATE failed:', e);
      showAlert('Change request failed. Check console.', 'error');
    }
  }

  async function handleDeleteCar(id) {
    if (!canEdit) return;

    const confirmed = await openConfirm({
      title: 'Delete car',
      message: 'Are you sure you want to delete this car?',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
    });
    if (!confirmed) return;

    if (isAdmin) {
      try {
        setCarsLoading(true);
        await client.graphql({
          query: DeleteCar,
          variables: { input: { id } },
          authMode: 'userPool',
        });
        setCars(prev => prev.filter(c => c.id !== id));
        if (carEditingId === id) {
          resetCarForm();
        }
        showAlert('Car deleted.', 'success');
      } catch (e) {
        console.error('DeleteCar failed:', e);
        showAlert('Delete failed. Check console.', 'error');
      } finally {
        setCarsLoading(false);
      }
      return;
    }

    try {
      await proposeCarChange(client, claims, 'DELETE', id, {});
      showAlert('Delete request submitted for approval.', 'success');
    } catch (e) {
      console.error('proposeCarChange DELETE failed:', e);
      showAlert('Delete request failed. Check console.', 'error');
    }
  }

  async function startEditCar(row) {
    setCarEditingId(row.id);
    setCarForm({
      CompanyId: row.CompanyId || (isAdmin ? '' : (myCompanyId || '')),
      name: row.name || '',
      plate: row.plate || '',
      active: !!row.active,
      fuelType: row.fuelType || 'PETROL',
      transmission: row.transmission || 'AUTOMATIC',
      year: row.year || THIS_YEAR,
      category: row.category || 'ECONOMY',
      bodyType: row.bodyType || 'HATCHBACK',
      color: row.color || 'White',

      startingPrice: (row.startingPrice ?? '') === null ? '' : String(row.startingPrice ?? ''),
      seats: (row.seats ?? '') === null ? '' : String(row.seats ?? ''),
    });


    // clear any previous local preview
    if (imagePreviewUrl) {
      try {
        URL.revokeObjectURL(imagePreviewUrl);
      } catch {
        // ignore
      }
    }
    setImageFile(null);
    setImagePreviewUrl(null);

    // load existing image from S3 if present
    if (row.imageKey) {
      try {
        const { url } = await getUrl({ key: row.imageKey });
        setImagePreviewUrl(url.toString());
      } catch (err) {
        console.error('Failed to load existing car image', err);
      }
    }
  }

  /* ===================== Render ===================== */
  const currencyByCompanyId = useMemo(() => {
    const map = new Map();
    for (const c of companies) {
      map.set(c.id, currencyForCountry(c.country));
    }
    return map;
  }, [companies]);

  const currencyForCompanyId = useCallback(
    (companyId) => currencyByCompanyId.get(companyId) || 'USD',
    [currencyByCompanyId]
  );
  if (!ready || (isAdmin && loadingCompany)) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ fontSize: 14, color: '#6b7280' }}>Loading…</div>
      </div>
    );
  }

  if (isAdmin && !activeCompanyId) {
    return (
      <div style={{ padding: 24 }}>
        <b>No active company.</b>
        <p style={{ fontSize: 13, color: '#6b7280' }}>
          No company resolved for the current country. Check your Company.country values
          or the country toggle.
        </p>
      </div>
    );
  }


  return (
    <div style={{ background: COLORS.gray[50], minHeight: 'calc(100vh - 64px)', position: 'relative' }}>
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: isMobile ? '16px 10px 28px' : '28px 20px 48px',
          display: 'grid',
          gap: 20,
        }}
      >
        {/* Header */}
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'baseline', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: COLORS.gray[900] }}>Fleet Management</h3>
            <p style={{ margin: '6px 0 0', fontSize: 15, color: COLORS.gray[600], lineHeight: 1.5 }}>
              Manage cars for your agency. Service logs, Greenway inspections, and maintenance schedules have moved to the dedicated Maintenance page.
            </p>
          </div>
          <Link
            to="/console/maintenance"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 14px',
              borderRadius: 10,
              background: COLORS.primaryLight,
              border: '1px solid #bfdbfe',
              color: COLORS.primaryDark,
              fontSize: 13,
              fontWeight: 700,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            <Wrench size={15} />
            Open Maintenance & Greenway →
          </Link>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              padding: '6px 14px',
              borderRadius: 8,
              border: `1px solid ${canEdit ? '#dcfce7' : COLORS.gray[300]}`,
              background: canEdit ? '#dcfce7' : COLORS.gray[100],
              color: canEdit ? '#166534' : COLORS.gray[600],
              whiteSpace: 'nowrap',
            }}
          >
            {canEdit ? '✓ Can Manage' : '○ View Only'}
          </span>
        </header>

        {/* === CARS FORM === */}
        <div ref={carFormRef}>
          <CarFormPanel
            isMobile={isMobile}
            isAdmin={isAdmin}
            canEdit={canEdit}
            companies={companies}
            companyName={companyName}
            carForm={carForm}
            setCarForm={setCarForm}
            carEditingId={carEditingId}
            carsLoading={carsLoading}
            onSubmitCreate={submitCreateCar}
            onSubmitUpdate={submitUpdateCar}
            onReset={resetCarForm}
            imagePreviewUrl={imagePreviewUrl}
            onImageChange={handleImageChange}
            currencyForCompanyId={currencyForCompanyId}
          />
        </div>

        {/* === CARS LIST === */}
        <CarListSection
          isMobile={isMobile}
          isAdmin={isAdmin}
          canEdit={canEdit}
          companies={companies}
          carsLoading={carsLoading}
          filteredCars={filteredCars}
          pagedCars={pagedCars}
          carFilters={carFilters}
          setCarFilters={setCarFilters}
          carPage={carPage}
          totalCarPages={totalCarPages}
          setCarPage={setCarPage}
          coName={coName}
          startEditCar={startEditCar}
          handleDeleteCar={handleDeleteCar}
          onOpenImage={openLightbox}
          currencyForCompanyId={currencyForCompanyId}
        />

        {/* === MAINTENANCE SECTION (Moved to /console/maintenance) === */}
        <section style={cardStyle()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <h4 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: COLORS.gray[900], display: 'flex', alignItems: 'center', gap: 8 }}>
                <Wrench size={18} color={COLORS.primary} />
                Maintenance Logs, Greenway PTI & Schedule
              </h4>
              <p style={{ fontSize: 13, color: COLORS.gray[600], margin: '4px 0 0' }}>
                Oil change & service logs, Georgian Greenway technical inspection deadlines, email reminders, and calendar maintenance blocks are managed in the Maintenance console.
              </p>
            </div>
            <Link
              to="/console/maintenance"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 16px',
                borderRadius: 10,
                background: COLORS.primary,
                color: '#ffffff',
                fontSize: 13,
                fontWeight: 700,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              <Wrench size={15} />
              Go to Maintenance Page
            </Link>
          </div>        </section>
      </div>
      <ImageLightbox
        src={lightbox.open ? lightbox.src : null}
        alt={lightbox.alt}
        onClose={closeLightbox}
      />
      {/* Toast alert */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            right: 16,
            bottom: 16,
            zIndex: 9999,
            maxWidth: 360,
          }}
        >
          <div
            style={{
              borderRadius: 10,
              padding: '12px 14px',
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
              boxShadow: '0 10px 30px rgba(15,23,42,0.2), 0 2px 8px rgba(0,0,0,0.1)',
              border: 'none',
              background:
                toast.variant === 'success'
                  ? '#d1fae5'
                  : toast.variant === 'error'
                    ? '#fee2e2'
                    : '#f0f9ff',
              color:
                toast.variant === 'success'
                  ? '#065f46'
                  : toast.variant === 'error'
                    ? '#7f1d1d'
                    : '#0c4a6e',
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            <div style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>
              {toast.variant === 'success' ? '✓' : toast.variant === 'error' ? '✕' : 'ℹ︎'}
            </div>
            <div style={{ flex: 1, lineHeight: 1.4 }}>{toast.message}</div>
            <button
              onClick={hideAlert}
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                padding: '2px',
                margin: '-2px 0 0 0',
                fontSize: 14,
                lineHeight: 1,
                color: 'inherit',
                opacity: 0.6,
                transition: 'opacity 0.2s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Confirm dialog */}
      {confirmState && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 9998,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(1px)',
            animation: 'fadeIn 0.2s ease-out',
          }}
          onClick={() => handleConfirm(false)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: 16,
              padding: 24,
              minWidth: 300,
              maxWidth: 420,
              boxShadow: '0 20px 50px rgba(0,0,0,0.15), 0 10px 20px rgba(0,0,0,0.08)',
              border: `1px solid ${COLORS.gray[200]}`,
              animation: 'slideUp 0.3s ease-out',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h4 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: COLORS.gray[900] }}>{confirmState.title}</h4>
            <p style={{ margin: '0 0 18px', fontSize: 14, color: COLORS.gray[600], lineHeight: 1.5 }}>
              {confirmState.message}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Button
                onClick={() => handleConfirm(false)}
                style={{ background: 'white' }}
              >
                {confirmState.cancelLabel}
              </Button>
              <DangerButton onClick={() => handleConfirm(true)}>
                {confirmState.confirmLabel}
              </DangerButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
